// LUMINA · components/EnergyForecastPanel.jsx
// Phase 104+: 能量前瞻面板 — 7天/1个月/1季度 能量节点 + 冲合预警 + 行动建议

import React, { useState, useEffect, useMemo } from 'react';
import { API } from '../constants/config';

const GD = "#D4AF37";
const GDD = "#B8940A";
const TXT = "rgba(0,0,0,0.82)";
const TXT2 = "rgba(0,0,0,0.45)";
const TXT3 = "rgba(0,0,0,0.40)";

const SCORE_COLOR = (s) => {
  if (s >= 80) return "#064E3B";
  if (s >= 60) return "#D4AF37";
  if (s >= 40) return "#8B6914";
  return "#9CA3AF";
};

const TAG_COLOR = {
  "气运亨通": "#064E3B",
  "平稳中正": "#6B4F10",
  "含藏蓄势": "#92400e",
  "静养蓄势": "#9CA3AF",
};

const WEEKDAYS_ZH = ["日", "一", "二", "三", "四", "五", "六"];

// ── Helpers ──────────────────────────────────────────────────────────────────

function dayLabel(dateStr) {
  const d = new Date(dateStr + "T00:00:00");
  return { month: d.getMonth() + 1, day: d.getDate(), weekday: WEEKDAYS_ZH[d.getDay()] };
}

function isToday(dateStr) {
  const d = new Date(dateStr + "T00:00:00");
  const t = new Date();
  return d.getFullYear() === t.getFullYear() &&
         d.getMonth() === t.getMonth() &&
         d.getDate() === t.getDate();
}

function isWeekend(dateStr) {
  const d = new Date(dateStr + "T00:00:00");
  return d.getDay() === 0 || d.getDay() === 6;
}

// ── Main Component ───────────────────────────────────────────────────────────

export default function EnergyForecastPanel({ birthData, token }) {
  const [tab, setTab] = useState("7d");
  const [weekly, setWeekly] = useState(null);
  const [monthly, setMonthly] = useState(null);
  const [quarter, setQuarter] = useState(null);
  const [criticalDates, setCriticalDates] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Serialize birthData to catch any field change (hour, gender, etc.)
  const birthKey = useMemo(() => birthData ? JSON.stringify(birthData) : null, [birthData]);

  // Fetch all data on mount — independent per endpoint, so one failure doesn't block others
  useEffect(() => {
    if (!birthData || !token) return;
    setLoading(true);
    setError("");

    const body = JSON.stringify({
      birth_year: birthData.year, birth_month: birthData.month,
      birth_day: birthData.day,   birth_hour: birthData.hour,
      gender: birthData.gender || "male",
    });
    const headers = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };
    const year = new Date().getFullYear();

    let completed = 0;
    const checkDone = () => { completed++; if (completed >= 4) setLoading(false); };

    // Weekly
    fetch(`${API}/audit/daily-energy/weekly`, { method: "POST", headers, body })
      .then(r => r.ok ? r.json() : r.json().then(d => Promise.reject(d)))
      .then(d => { setWeekly(d); checkDone(); })
      .catch(e => { setError(prev => prev ? prev + "; 7天不可用" : "7天预测暂不可用"); checkDone(); });

    // 30-day
    fetch(`${API}/audit/forecast-30d`, { method: "POST", headers, body })
      .then(r => r.ok ? r.json() : r.json().then(d => Promise.reject(d)))
      .then(d => { setMonthly(d); checkDone(); })
      .catch(e => { setError(prev => prev ? prev + "; 月度不可用" : "月度预测暂不可用"); checkDone(); });

    // Quarter
    fetch(`${API}/audit/forecast-quarter`, { method: "POST", headers, body })
      .then(r => r.ok ? r.json() : r.json().then(d => Promise.reject(d)))
      .then(d => { setQuarter(d); checkDone(); })
      .catch(e => { setError(prev => prev ? prev + "; 季度不可用" : "季度预测暂不可用"); checkDone(); });

    // Critical dates — fallback: if auth fails, proceed without clash warnings
    fetch(`${API}/audit/critical-dates?year=${year}`, { headers })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setCriticalDates(d); checkDone(); })
      .catch(() => { checkDone(); });  // silent — clash warnings are optional
  }, [token, birthKey]);

  // ── Merge critical dates into daily forecast ────────────────────────────────
  const criticalMap = useMemo(() => {
    if (!criticalDates) return {};
    const map = {};
    const all = [
      ...(criticalDates.clash_days || []),
      ...(criticalDates.combine_days || []),
      ...(criticalDates.fuyin_days || []),
    ];
    all.forEach(cd => {
      if (!map[cd.date]) map[cd.date] = [];
      map[cd.date].push(cd);
    });
    return map;
  }, [criticalDates]);

  // ── Loading — show tabs immediately, data fills in as it arrives ────────────
  if (loading && !weekly && !monthly && !quarter) return (
    <div style={{
      background: "#fff", border: "1px solid rgba(212,175,55,0.12)",
      borderRadius: 16, padding: "48px 24px", textAlign: "center",
      boxShadow: "0 4px 20px rgba(132,107,50,0.04)",
    }}>
      <div style={{ fontSize: 28, marginBottom: 12 }}>🔮</div>
      <div style={{ fontSize: 13, color: TXT2, letterSpacing: 1 }}>推演能量节点…</div>
      <div style={{ fontSize: 11, color: TXT3, marginTop: 6 }}>基于命盘计算未来能量地图</div>
    </div>
  );

  if (!loading && !weekly && !monthly && !quarter && error) return (
    <div style={{
      background: "#fff", border: "1px solid rgba(212,175,55,0.12)",
      borderRadius: 16, padding: "32px 24px", textAlign: "center",
      boxShadow: "0 4px 20px rgba(132,107,50,0.04)",
    }}>
      <div style={{ fontSize: 13, color: "#92400e", letterSpacing: 1 }}>{error}</div>
    </div>
  );

  // ── Section wrapper ─────────────────────────────────────────────────────────
  return (
    <>
    <style>{`
      @media (max-width: 768px) {
        .efp-month-grid-row {
          flex-wrap: nowrap !important;
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
          padding-bottom: 4px;
        }
        .efp-month-grid-row > div {
          min-width: 38px;
          flex-shrink: 0;
        }
        .efp-quarter-cards {
          flex-direction: column;
          gap: 10px;
        }
        .efp-quarter-cards > div {
          min-width: 0;
        }
        .efp-summary-badges {
          gap: 8px;
        }
        .efp-bar-chart {
          gap: 3px;
        }
        .efp-legend {
          gap: 8px;
          font-size: 9px;
        }
      }
    `}</style>
    <div style={{
      background: "#fff", border: "1px solid rgba(212,175,55,0.12)",
      borderRadius: 16, padding: "28px 24px 28px",
      boxShadow: "0 4px 20px rgba(132,107,50,0.04)",
    }}>
      {/* Header + Tabs */}
      <div style={{ textAlign: "center", marginBottom: 20 }}>
        <div style={{
          fontSize: 12, color: GD, letterSpacing: 3, marginBottom: 4,
          fontFamily: "'Source Han Sans SC','Noto Sans CJK SC',sans-serif", fontWeight: 600,
        }}>
          🔮 能量前瞻
        </div>
        <div style={{ fontSize: 10, color: TXT3, letterSpacing: 0.5, marginBottom: 16 }}>
          基于你的命盘，精确计算能量节点 — 哪几天适合行动，哪几天宜静不宜动
        </div>

        {/* Tabs */}
        <div style={{ display: "flex", justifyContent: "center", gap: 4, marginBottom: 0 }}>
          {[
            { key: "7d", label: "7天", sub: "每日能量" },
            { key: "30d", label: "1个月", sub: "行动日历" },
            { key: "90d", label: "1季度", sub: "趋势窗口" },
          ].map(t => (
            <button key={t.key} onClick={() => setTab(t.key)} style={{
              padding: "8px 20px",
              border: tab === t.key ? `1px solid ${GD}` : "1px solid rgba(0,0,0,0.06)",
              borderRadius: 20,
              background: tab === t.key ? "rgba(212,175,55,0.08)" : "transparent",
              color: tab === t.key ? GD : TXT2,
              fontSize: 13, fontWeight: tab === t.key ? 600 : 400,
              cursor: "pointer", letterSpacing: 1,
              transition: "all .2s",
              fontFamily: "inherit",
            }}>
              {t.label}
              <span style={{ display: "block", fontSize: 9, fontWeight: 400, opacity: 0.6, marginTop: 1 }}>
                {t.sub}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* ── 7-Day View ──────────────────────────────────────────────────────── */}
      {tab === "7d" && (
        weekly ? <WeekView forecast={weekly.forecast} criticalMap={criticalMap} avgScore={weekly.avg_score} bestDays={weekly.best_days} cautionDays={weekly.caution_days} />
        : <TabLoading label="7天预测推演中…" />
      )}

      {/* ── 30-Day View ─────────────────────────────────────────────────────── */}
      {tab === "30d" && (
        monthly ? <MonthView forecast={monthly.forecast} criticalMap={criticalMap} avgScore={monthly.avg_score} daymaster={monthly.daymaster} strength={monthly.strength} narrative={monthly.narrative_zh} />
        : <TabLoading label="月度预测推演中…" />
      )}

      {/* ── 90-Day View ─────────────────────────────────────────────────────── */}
      {tab === "90d" && (
        quarter ? <QuarterView data={quarter} />
        : <TabLoading label="季度预测推演中…" />
      )}
    </div>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// 7-Day View
// ═══════════════════════════════════════════════════════════════════════════════

function WeekView({ forecast, criticalMap, avgScore, bestDays, cautionDays }) {
  if (!forecast?.length) return null;
  const maxScore = Math.max(...forecast.map(f => f.energy_score || 50));
  const todayNarrative = forecast[0]?.narrative_zh;

  return (
    <div>
      {/* ── AI 今日能量叙事 ── */}
      {todayNarrative && (
        <div style={{
          padding: "14px 16px", marginBottom: 16,
          background: "rgba(212,175,55,0.04)", border: "1px solid rgba(212,175,55,0.15)",
          borderRadius: 10, fontSize: 13, color: TXT, lineHeight: 1.9, letterSpacing: 0.3,
        }}>
          <div style={{ fontSize: 10, color: GD, fontWeight: 600, letterSpacing: 2, marginBottom: 6 }}>
            💡 今日能量解读
          </div>
          {todayNarrative}
        </div>
      )}

      {/* Score summary */}
      <div style={{ display: "flex", justifyContent: "center", gap: 16, marginBottom: 20, flexWrap: "wrap" }} className="efp-summary-badges">
        <SummaryBadge label="7日均分" value={avgScore} color={SCORE_COLOR(avgScore)} />
        <SummaryBadge label="气运亨通" value={bestDays?.length || 0} color="#064E3B" />
        <SummaryBadge label="含藏/静养" value={cautionDays?.length || 0} color="#9CA3AF" />
      </div>

      {/* Bar chart */}
      <div style={{ display: "flex", gap: 6, alignItems: "flex-end", minHeight: 120, marginBottom: 12 }} className="efp-bar-chart">
        {forecast.map((f, i) => {
          const d = new Date(f.date + "T00:00:00");
          const today = isToday(f.date);
          const crits = criticalMap[f.date] || [];
          const hasClash = crits.some(c => c.type === "clash" && c.severity === "high");
          const barH = Math.max(20, ((f.energy_score || 50) / maxScore) * 100);
          const color = SCORE_COLOR(f.energy_score);
          const actTag = f.energy_score >= 80 ? "✓" : f.energy_score < 40 ? "✗" : "";

          return (
            <div key={f.date} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 3 }}>
              {/* Action tag */}
              <div style={{ fontSize: 10, color: actTag === "✓" ? "#059669" : actTag === "✗" ? "#dc2626" : "transparent", fontWeight: 700, height: 14 }}>
                {actTag}
              </div>
              {/* Score */}
              <div style={{ fontSize: 11, fontWeight: 600, color, letterSpacing: 0.3 }}>
                {f.energy_score || 50}
              </div>
              {/* Bar — clash days: desaturate + neutral gradient, kills "high score + clash" visual conflict */}
              <div style={{
                width: "100%", maxWidth: 32, height: barH,
                borderRadius: "4px 4px 0 0",
                background: hasClash
                  ? "linear-gradient(180deg, #A99260 0%, rgba(169,146,96,0.25) 100%)"
                  : color,
                opacity: today ? 1 : 0.72,
                filter: hasClash ? "grayscale(30%)" : "none",
                boxShadow: hasClash
                  ? "0 0 0 1px #dc262640, inset 0 1px 0 #dc262660"
                  : today ? `0 0 0 2px ${color}40` : "none",
                position: "relative",
              }}>
                {/* Clash indicator dot */}
                {hasClash && (
                  <div style={{
                    position: "absolute", top: -8, left: "50%", transform: "translateX(-50%)",
                    width: 6, height: 6, borderRadius: "50%", background: "#dc2626",
                  }} />
                )}
              </div>
              {/* Day label — clash days get warning tone */}
              <div style={{
                fontSize: 11, fontWeight: today ? 700 : 400,
                color: hasClash ? "#b91c1c" : today ? GD : TXT2, letterSpacing: 0.5,
              }}>
                {today ? "今" : WEEKDAYS_ZH[d.getDay()]}
                {hasClash ? "⚡" : ""}
              </div>
              {/* Score — clash days: muted, don't show a high number in gold */}
              {hasClash && (
                <div style={{ fontSize: 8, color: "#b91c1c", letterSpacing: 0.3, marginTop: 1 }}>
                  冲
                </div>
              )}
              <div style={{ fontSize: 9, color: TXT3 }}>
                {d.getMonth() + 1}/{d.getDate()}
              </div>
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap", fontSize: 10, color: TXT2, marginBottom: 12 }} className="efp-legend">
        {[
          ["#064E3B", "气运亨通 80+"],
          ["#D4AF37", "平稳中正 60-79"],
          ["#8B6914", "含藏蓄势 40-59"],
          ["#9CA3AF", "静养蓄势 <40"],
        ].map(([c, l]) => (
          <span key={l} style={{ display: "flex", alignItems: "center", gap: 3 }}>
            <span style={{ width: 8, height: 8, borderRadius: 2, background: c, display: "inline-block" }} />
            {l}
          </span>
        ))}
        <span style={{ display: "flex", alignItems: "center", gap: 3 }}>
          <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#dc2626", display: "inline-block" }} />
          冲日预警
        </span>
      </div>

      {/* Clash/Critical alerts */}
      <CriticalAlerts forecast={forecast} criticalMap={criticalMap} days={7} />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// 30-Day View
// ═══════════════════════════════════════════════════════════════════════════════

function MonthView({ forecast, criticalMap, avgScore, daymaster, strength, narrative }) {
  if (!forecast?.length) return null;

  // Group by week
  const weeks = [];
  for (let i = 0; i < forecast.length; i += 7) {
    weeks.push(forecast.slice(i, i + 7));
  }

  // Action summary — thresholds match energy_pulse labels
  const actionDays = forecast.filter(f => f.score >= 80).length;
  const cautionDays = forecast.filter(f => f.score < 40).length;
  const bestDays = forecast.filter(f => f.score >= 80).slice(0, 5);

  return (
    <div>
      {/* ── AI 30天能量总结 ── */}
      {narrative && (
        <div style={{
          padding: "14px 16px", marginBottom: 18,
          background: "rgba(212,175,55,0.04)", border: "1px solid rgba(212,175,55,0.15)",
          borderRadius: 10, fontSize: 13, color: TXT, lineHeight: 1.9, letterSpacing: 0.3,
        }}>
          <div style={{ fontSize: 10, color: GD, fontWeight: 600, letterSpacing: 2, marginBottom: 6 }}>
            💡 30天能量洞察
          </div>
          {narrative}
        </div>
      )}

      {/* Summary row */}
      <div style={{ display: "flex", justifyContent: "center", gap: 12, marginBottom: 18, flexWrap: "wrap" }} className="efp-summary-badges">
        <SummaryBadge label="30日均分" value={avgScore} color={SCORE_COLOR(avgScore)} />
        <SummaryBadge label={`气运亨通 ${actionDays}天`} value="" color="#064E3B" />
        <SummaryBadge label={`静养蓄势 ${cautionDays}天`} value="" color="#9CA3AF" />
        <SummaryBadge label={`日主${daymaster}`} value={strength} color={GD} />
      </div>

      {/* Week-by-week calendar */}
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {weeks.map((week, wi) => (
          <div key={wi}>
            <div style={{ fontSize: 10, color: TXT2, letterSpacing: 1, marginBottom: 6, paddingLeft: 2 }}>
              第{wi + 1}周 · {week[0]?.date?.slice(5)} ~ {week[week.length - 1]?.date?.slice(5)}
            </div>
            <div style={{ display: "flex", gap: 4 }} className="efp-month-grid-row">
              {week.map((d, di) => {
                const crits = criticalMap[d.date] || [];
                const hasClash = crits.some(c => c.type === "clash" && c.severity === "high");
                const hasCombine = crits.some(c => c.type === "combine");
                const dl = dayLabel(d.date);
                const tagColor = TAG_COLOR[d.tag] || TXT2;

                return (
                  <div key={d.date} title={d.tag + (hasClash ? " ⚡冲日" : "")} style={{
                    flex: 1, minWidth: 0,
                    padding: "6px 4px 8px",
                    borderRadius: 8,
                    background: hasClash ? "rgba(220,38,38,0.06)" : isToday(d.date) ? "rgba(212,175,55,0.1)" : isWeekend(d.date) ? "rgba(132,107,50,0.03)" : "rgba(132,107,50,0.04)",
                    border: hasClash ? "1px solid rgba(220,38,38,0.25)" : isToday(d.date) ? `1px solid ${GD}40` : "1px solid rgba(132,107,50,0.08)",
                    textAlign: "center",
                    cursor: "default",
                    position: "relative",
                  }}>
                    {/* Clash/Combine indicator */}
                    {(hasClash || hasCombine) && (
                      <div style={{
                        position: "absolute", top: 2, right: 3,
                        fontSize: 8, lineHeight: 1,
                      }}>
                        {hasClash ? "⚡" : "🔗"}
                      </div>
                    )}
                    {/* Weekday */}
                    <div style={{ fontSize: 10, color: TXT3, marginBottom: 2, fontWeight: 500 }}>
                      {isToday(d.date) ? "今" : dl.weekday}
                    </div>
                    {/* Date — clash days suppress optimistic green/gold, show neutral */}
                    <div style={{
                      fontSize: 16, fontWeight: isToday(d.date) ? 700 : 500,
                      color: hasClash ? "#991b1b" : tagColor, letterSpacing: 0.3,
                    }}>
                      {dl.day}
                    </div>
                    {/* Score */}
                    <div style={{ fontSize: 9, color: TXT3, marginTop: 1 }}>
                      {d.score}
                    </div>
                    {/* Action tag */}
                    <div style={{
                      fontSize: 10, fontWeight: 700, color: tagColor,
                      marginTop: 3, letterSpacing: 0.3,
                      background: `${tagColor}14`, borderRadius: 4,
                      padding: "2px 0", whiteSpace: "nowrap",
                    }}>
                      {d.tag}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Best action days highlight */}
      {bestDays.length > 0 && (
        <div style={{
          marginTop: 16, padding: "14px 16px",
          background: "rgba(5,150,105,0.04)", border: "1px solid rgba(5,150,105,0.15)",
          borderRadius: 10,
        }}>
          <div style={{ fontSize: 11, color: "#059669", fontWeight: 600, letterSpacing: 1, marginBottom: 8 }}>
            ✅ 最佳行动窗口
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {bestDays.map(d => {
              const dl = dayLabel(d.date);
              return (
                <span key={d.date} style={{
                  fontSize: 12, color: "#059669", fontWeight: 500,
                  background: "rgba(5,150,105,0.08)", padding: "4px 10px",
                  borderRadius: 12, letterSpacing: 0.5,
                }}>
                  {dl.month}/{dl.day} {dl.weekday} ({d.score})
                </span>
              );
            })}
          </div>
        </div>
      )}

      {/* Clash/Critical alerts */}
      <CriticalAlerts forecast={forecast} criticalMap={criticalMap} days={30} />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// 90-Day View
// ═══════════════════════════════════════════════════════════════════════════════

function QuarterView({ data }) {
  if (!data) return null;
  const { months, weeks, quarter_trend, period, daymaster, strength, xiyong, quarter_clash } = data;
  const narrative = data.narrative_zh;

  const trendIcon = quarter_trend?.includes("↑") ? "📈" : quarter_trend?.includes("↓") ? "📉" : "➡️";

  // Build per-month clash lookup from quarter_clash metadata
  const clashByLabel = {};
  if (quarter_clash?.per_month) {
    quarter_clash.per_month.forEach(pm => { clashByLabel[pm.label] = pm; });
  }

  return (
    <div>
      {/* Quarter header */}
      <div style={{ textAlign: "center", marginBottom: 16 }}>
        <div style={{ fontSize: 11, color: TXT2, letterSpacing: 1 }}>
          {period}
        </div>
        <div style={{ fontSize: 18, color: TXT, fontWeight: 600, letterSpacing: 1, marginTop: 6 }}>
          {trendIcon} {quarter_trend}
        </div>
        <div style={{ fontSize: 10, color: TXT3, marginTop: 4 }}>
          日主{daymaster} · {strength}
          {(xiyong?.xi_shen || xiyong?.yong_shen) && (
            <> · 喜{xiyong.xi_shen || ""}{xiyong.yong_shen ? "/" + xiyong.yong_shen : ""}</>
          )}
        </div>
        {/* Quarter-level clash flag */}
        {quarter_clash?.quarter_flag && (
          <div style={{
            marginTop: 8, display: "inline-block",
            padding: "3px 14px", borderRadius: 12,
            background: "rgba(220,38,38,0.08)", border: "1px solid rgba(220,38,38,0.2)",
            fontSize: 11, color: "#991b1b", fontWeight: 600, letterSpacing: 1,
          }}>
            ⚡ {quarter_clash.quarter_flag}
          </div>
        )}
      </div>

      {/* ── AI 季度能量洞察 ── */}
      {narrative && (
        <div style={{
          padding: "14px 16px", marginBottom: 20,
          background: "rgba(212,175,55,0.04)", border: "1px solid rgba(212,175,55,0.15)",
          borderRadius: 10, fontSize: 13, color: TXT, lineHeight: 1.9, letterSpacing: 0.3,
        }}>
          <div style={{ fontSize: 10, color: GD, fontWeight: 600, letterSpacing: 2, marginBottom: 6 }}>
            💡 季度能量洞察
          </div>
          {narrative}
        </div>
      )}

      {/* Weekly trend bars */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 10, color: TXT2, letterSpacing: 2, marginBottom: 10 }}>
          📊 周均能量趋势
        </div>
        <div style={{ display: "flex", gap: 4, alignItems: "flex-end", minHeight: 80 }}>
          {(weeks || []).map((w, i) => {
            const h = Math.max(12, (w.avg_score / 90) * 80);
            const color = SCORE_COLOR(w.avg_score);
            return (
              <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                <div style={{ fontSize: 9, color, fontWeight: 600 }}>
                  {w.avg_score}
                </div>
                <div style={{
                  width: "100%", maxWidth: 28, height: h,
                  borderRadius: "3px 3px 0 0",
                  background: color, opacity: 0.75,
                }} />
                <div style={{ fontSize: 9, color: TXT3, marginTop: 2 }}>
                  W{i + 1}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Monthly summary cards — with clash badges */}
      <div style={{ display: "flex", gap: 12, marginBottom: 16 }} className="efp-quarter-cards">
        {(months || []).map((m, i) => {
          const cm = clashByLabel[m.label];
          const hasClash = cm && cm.high_clash > 0;
          return (
            <div key={i} style={{
              flex: 1, padding: "14px 12px", position: "relative",
              background: hasClash ? "rgba(220,38,38,0.04)" : "rgba(212,175,55,0.03)",
              border: hasClash ? "1px solid rgba(220,38,38,0.18)" : "1px solid rgba(212,175,55,0.12)",
              borderRadius: 10, textAlign: "center",
            }}>
              <div style={{ fontSize: 10, color: TXT2, letterSpacing: 1, marginBottom: 4 }}>
                {m.label}
              </div>
              <div style={{ fontSize: 22, fontWeight: 600, color: SCORE_COLOR(m.avg_score), letterSpacing: 0.5 }}>
                {m.avg_score}
              </div>
              <div style={{
                fontSize: 11, fontWeight: 600, marginTop: 4,
                color: m.action_zone === "宜行动" ? "#059669" : "#8B6914",
                letterSpacing: 1,
              }}>
                {m.action_zone === "宜行动" ? "✅ 宜行动" : "🌙 宜静守"}
              </div>
              {/* Clash badge */}
              {cm && (cm.high_clash > 0 || cm.combine > 0 || cm.fuyin > 0) && (
                <div style={{ marginTop: 8, fontSize: 9, color: "#991b1b", letterSpacing: 0.5 }}>
                  {cm.high_clash > 0 && <span>⚡×{cm.high_clash} </span>}
                  {cm.combine > 0 && <span>🔗×{cm.combine} </span>}
                  {cm.fuyin > 0 && <span>🔄×{cm.fuyin}</span>}
                </div>
              )}
              {/* Key clash dates */}
              {cm && cm.key_dates?.length > 0 && (
                <div style={{ marginTop: 4, display: "flex", flexWrap: "wrap", gap: 3, justifyContent: "center" }}>
                  {cm.key_dates.map(dd => (
                    <span key={dd} style={{
                      fontSize: 8, color: "#dc2626", fontWeight: 600,
                      background: "rgba(220,38,38,0.08)", padding: "1px 5px",
                      borderRadius: 4,
                    }}>
                      {dd}
                    </span>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Quarter clash summary */}
      {quarter_clash && (quarter_clash.high_clash_count > 0 || quarter_clash.combine_count > 0 || quarter_clash.fuyin_count > 0) && (
        <div style={{
          marginBottom: 16, padding: "14px 16px",
          background: "rgba(220,38,38,0.03)", border: "1px solid rgba(220,38,38,0.12)",
          borderRadius: 10, fontSize: 12, color: TXT, lineHeight: 1.8,
        }}>
          <div style={{ fontSize: 10, color: "#92400e", fontWeight: 600, letterSpacing: 2, marginBottom: 6 }}>
            ⚠ 季度冲合
          </div>
          <div style={{ fontSize: 11, color: "#991b1b", letterSpacing: 0.3 }}>
            本季度共 <b>{quarter_clash.high_clash_count}</b> 个高危冲日
            {quarter_clash.combine_count > 0 && `、${quarter_clash.combine_count} 个合日`}
            {quarter_clash.fuyin_count > 0 && `、${quarter_clash.fuyin_count} 个伏吟日`}。
            {quarter_clash.high_clash_count >= 10
              ? "冲克密集，重大决策宜避开冲日窗口。"
              : quarter_clash.high_clash_count >= 5
              ? "留意每月冲日，提前规划关键事项。"
              : "数量可控，个别冲日不影响整体节奏。"}
          </div>
        </div>
      )}

      {/* Key insight */}
      <div style={{
        padding: "14px 16px",
        background: "rgba(212,175,55,0.04)", border: "1px solid rgba(212,175,55,0.15)",
        borderRadius: 10, fontSize: 12, color: TXT, lineHeight: 1.8,
      }}>
        <div style={{ fontSize: 10, color: GD, fontWeight: 600, letterSpacing: 2, marginBottom: 6 }}>
          💡 季度洞察
        </div>
        {quarter_trend?.includes("↑") && (
          "本季度能量整体上扬，前两个月为行动黄金期。把握能量窗口推进重要事项，第三个月宜收尾巩固。"
        )}
        {quarter_trend?.includes("→") && (
          "本季度能量平稳，宜在日常积累中稳步推进。关键节点集中在节气前后，留意能量切换带来的调整窗口。"
        )}
        {quarter_trend?.includes("↓") && (
          "本季度能量趋于收敛，前期可适度行动，后期宜转为内守和修整。收敛期是梳理和准备的良机。"
        )}
      </div>

      {/* ── 气运 Badge 行 ──────────────────────────────────────────────────── */}
      {quarter_clash && (
        <div style={{
          marginTop: 14, display: "flex", gap: 8, flexWrap: "wrap",
          justifyContent: "center",
        }}>
          {quarter_clash.quarter_flag && (
            <span style={{
              fontSize: 10, color: "#b91c1c", fontWeight: 600,
              background: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.2)",
              padding: "3px 12px", borderRadius: 14, letterSpacing: "0.05em",
            }}>
              🟡 {quarter_clash.quarter_flag}
            </span>
          )}
          {quarter_clash.high_clash_count > 0 && (
            <span style={{
              fontSize: 10, color: "#8B6914", fontWeight: 600,
              background: "rgba(139,105,20,0.06)", border: "1px solid rgba(139,105,20,0.2)",
              padding: "3px 12px", borderRadius: 14, letterSpacing: "0.05em",
            }}>
              ⚡ {quarter_clash.high_clash_count}个冲日
            </span>
          )}
          {quarter_clash.combine_count > 0 && (
            <span style={{
              fontSize: 10, color: "#0F766E", fontWeight: 600,
              background: "rgba(15,118,110,0.06)", border: "1px solid rgba(15,118,110,0.2)",
              padding: "3px 12px", borderRadius: 14, letterSpacing: "0.05em",
            }}>
              🔗 {quarter_clash.combine_count}个合日
            </span>
          )}
          {quarter_clash.fuyin_count > 0 && (
            <span style={{
              fontSize: 10, color: "#7c3aed", fontWeight: 600,
              background: "rgba(124,58,237,0.06)", border: "1px solid rgba(124,58,237,0.2)",
              padding: "3px 12px", borderRadius: 14, letterSpacing: "0.05em",
            }}>
              🔄 {quarter_clash.fuyin_count}个伏吟
            </span>
          )}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// Shared sub-components
// ═══════════════════════════════════════════════════════════════════════════════

function SummaryBadge({ label, value, color }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 6,
      background: `${color}08`, border: `1px solid ${color}20`,
      borderRadius: 10, padding: "6px 14px",
    }}>
      <span style={{ fontSize: 10, color: TXT2, letterSpacing: 0.5 }}>{label}</span>
      {value !== "" && (
        <span style={{ fontSize: 15, fontWeight: 700, color, letterSpacing: 0.5 }}>{value}</span>
      )}
    </div>
  );
}

function CriticalAlerts({ forecast, criticalMap, days }) {
  // Collect critical dates within range
  const alerts = [];
  const seen = new Set();
  (forecast || []).forEach(f => {
    const crits = criticalMap[f.date] || [];
    crits.forEach(c => {
      const key = `${c.date}-${c.type}-${c.pillar}`;
      if (!seen.has(key)) {
        seen.add(key);
        alerts.push({ ...c, date: f.date });
      }
    });
  });

  if (alerts.length === 0) return null;

  // Group by severity
  const highAlerts = alerts.filter(a => a.severity === "high" && a.type === "clash");
  const medAlerts = alerts.filter(a => a.severity !== "high" || a.type !== "clash");

  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ fontSize: 10, color: "#92400e", letterSpacing: 2, marginBottom: 10, fontWeight: 600 }}>
        ⚠ 冲合预警 ({alerts.length}个节点)
      </div>

      {/* High-severity clash alerts */}
      {highAlerts.length > 0 && (
        <div style={{
          background: "rgba(220,38,38,0.04)", border: "1px solid rgba(220,38,38,0.15)",
          borderRadius: 10, padding: "12px 16px", marginBottom: 8,
        }}>
          <div style={{ fontSize: 10, color: "#dc2626", fontWeight: 600, letterSpacing: 1, marginBottom: 6 }}>
            🔴 冲日预警 — 宜静不宜动
          </div>
          {highAlerts.slice(0, 8).map((a, i) => {
            const dl = dayLabel(a.date);
            return (
              <div key={i} style={{ fontSize: 11, color: "#991b1b", marginBottom: 3, letterSpacing: 0.3 }}>
                <span style={{ fontWeight: 600 }}>{dl.month}/{dl.day} {dl.weekday}</span>
                {" "}{a.description?.slice(0, 40)}
              </div>
            );
          })}
        </div>
      )}

      {/* Medium/low alerts */}
      {medAlerts.length > 0 && (
        <div style={{
          background: "rgba(212,175,55,0.04)", border: "1px solid rgba(212,175,55,0.15)",
          borderRadius: 10, padding: "12px 16px",
        }}>
          <div style={{ fontSize: 10, color: GDD, fontWeight: 600, letterSpacing: 1, marginBottom: 6 }}>
            🟡 合/伏吟 — 能量交汇
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {medAlerts.slice(0, 12).map((a, i) => {
              const dl = dayLabel(a.date);
              const typeLabel = a.type === "combine" ? "合" : a.type === "fuyin" ? "伏" : "";
              return (
                <span key={i} style={{
                  fontSize: 10, color: GDD,
                  background: "rgba(184,148,10,0.06)", padding: "2px 8px",
                  borderRadius: 8, letterSpacing: 0.3,
                }}>
                  {dl.month}/{dl.day} {typeLabel}
                </span>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function TabLoading({ label }) {
  return (
    <div style={{ textAlign: "center", padding: "32px 0", color: TXT2, fontSize: 13, letterSpacing: 1 }}>
      🔮 {label}
    </div>
  );
}
